import { useEffect, useState } from "react";
import { APP_VERSION } from "../appVersion";

/**
 * useVersionCheck — detecta que saiu um deploy novo enquanto a aba está aberta.
 *
 * Como funciona: `scripts/set-version.js` (npm prebuild) carimba o MESMO
 * identificador em dois lugares — `src/appVersion.js` (vai pro bundle, é a
 * versão que ESTA aba está rodando) e `public/version.json` (vira
 * `build/version.json`, é o que o servidor está entregando agora). Divergiu →
 * tem versão nova no ar.
 *
 * O `version.json` é servido sem cache (header em `vercel.json`); sem isso a
 * checagem responderia sempre a versão antiga e a feature viraria no-op.
 *
 * Regras:
 * - Checa a cada 60s, e também em `focus` / `visibilitychange` (visível) — o
 *   caso comum é a aba ficar horas em background e o usuário voltar nela.
 * - Em dev (`APP_VERSION === "dev"`) nunca avisa e nem faz requisição.
 * - Ao detectar, para de checar (interval + listeners removidos): o aviso já
 *   está na tela, insistir não muda nada.
 * - Qualquer falha (rede, 404, JSON inválido, resposta não-ok) é silenciosa.
 *   Um erro de checagem de versão jamais pode quebrar a UI nem poluir o console.
 */
const CHECK_INTERVAL_MS = 60_000;
const VERSION_URL = "/version.json";

export function useVersionCheck() {
  const [state, setState] = useState({
    updateAvailable: false,
    latestVersion: null,
  });

  useEffect(() => {
    // Build local: não há deploy para comparar.
    if (APP_VERSION === "dev") return undefined;

    // `active` protege contra setState após unmount — o StrictMode do React 19
    // monta/desmonta o efeito duas vezes em dev, e o fetch é assíncrono.
    let active = true;
    let intervalId = null;

    // Declarações de função (hoisted) porque `check` e `stop` se referenciam
    // mutuamente; ambas só são chamadas depois que o efeito termina de rodar.
    function stop() {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }

    function onVisibilityChange() {
      if (document.visibilityState === "visible") check();
    }

    async function check() {
      if (!active) return;
      try {
        const res = await fetch(VERSION_URL, { cache: "no-store" });
        if (!active || !res || !res.ok) return;

        const data = await res.json();
        if (!active) return;

        const remote = data && data.version;
        if (typeof remote !== "string" || remote === "") return;
        if (remote === APP_VERSION) return;

        setState({ updateAvailable: true, latestVersion: remote });
        stop();
      } catch {
        // Silencioso por design: offline, deploy em andamento, JSON parcial…
        // Nada disso é problema do usuário.
      }
    }

    intervalId = setInterval(check, CHECK_INTERVAL_MS);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisibilityChange);

    // Uma checagem imediata cobre a aba que já estava aberta quando o deploy saiu.
    check();

    return () => {
      active = false;
      stop();
    };
  }, []);

  return state;
}

export default useVersionCheck;
